import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import tradeService from '../../services/tradeService';
import navService from '../../services/navService';
import tradeSettingsService from '../../services/tradeSettingsService';
import transactionService from '../../services/transactionService';
import fxService from '../../services/fxService';
import benchmarkService from '../../services/benchmarkService';
import statsService from '../../services/statsService';

// Async thunk to fetch capital deployment data
export const fetchCapitalDeploymentData = createAsyncThunk(
  'dashboard/fetchCapitalDeployment',
  async ({ reportingCurrency = 'USD' }, { rejectWithValue }) => {
    try {
      const result = await tradeService.getCapitalDeployment({ reportingCurrency });
      return result.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch capital deployment data');
    }
  }
);

// Async thunk to fetch filter options (systems, setups from trade settings)
export const fetchFilterOptions = createAsyncThunk(
  'dashboard/fetchFilterOptions',
  async (_, { rejectWithValue }) => {
    try {
      // Fetch systems and setups from trade settings
      const [systemsRes, setupsRes] = await Promise.all([
        tradeSettingsService.getSettingsByType('system'),
        tradeSettingsService.getSettingsByType('setup')
      ]);

      // API returns { success: true, data: { settings: [...] } }
      // Each setting has: { id, type, value, description, ... }
      return {
        systems: systemsRes.data?.settings || [],
        setups: setupsRes.data?.settings || []
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch filter options');
    }
  }
);

// Async thunk to fetch dashboard metrics
//
// Dashboard audit 2026-07-11: filters are read from the STORE, not from a
// caller-supplied snapshot. The old pattern (callers passing {...filters,
// ...newFilters} from a render-time closure) could fetch with dropped filters
// when two changes landed in one render cycle, and made it possible for the
// fetched data to disagree with the labels (which always read the store).
// Callers now dispatch setFilters/clearFilters FIRST (synchronous), then this
// thunk — data and labels can no longer diverge.
export const fetchDashboardMetrics = createAsyncThunk(
  'dashboard/fetchMetrics',
  async ({ filters: filtersOverride, reportingCurrency = 'AUD' } = {}, { getState, rejectWithValue }) => {
    try {
      const filters = filtersOverride || getState().dashboard.filters || {};
      // Build date range params
      const dateParams = getDateRangeParams(filters.dateRange, filters.customStartDate, filters.customEndDate);

      // Fetch NAV data (unfiltered for FUM/CAGR) in the user's reporting currency
      // Use getCurrentFromSnapshots to match NAV page values (line-item approach)
      const navPromise = navService.getCurrentFromSnapshots({ reportingCurrency });

      // Fetch ALL trades first (we'll filter client-side for flexibility)
      // Pass reportingCurrency to get net_pl_reporting for each trade.
      // Item 85 P2d — rollup=strategies: multi-leg option strategies arrive
      // as ONE row each (P&L = Σ legs, R = defined risk), so win rate,
      // expectancy, Total R and the Four-Grid count a spread as one trade.
      const tradesPromise = tradeService.getTrades({ limit: 10000, reportingCurrency, rollup: 'strategies' });
      // rollup=strategies so Active Trades counts a spread as ONE position,
      // matching the closed-trade stats above (which already roll up) and the
      // Trades page. Without it a 2-leg vertical inflated the count by 1.
      const openTradesPromise = tradeService.getOpenTrades(null, { rollupStrategies: true });

      // Fetch unreconciled counts (trades = unmatched closes, transactions = unmatched opens)
      const unreconciledClosesPromise = tradeService.getAllUnmatchedCloses();
      const tradeTransactionsPromise = transactionService.getTradeTransactions();
      // Review queue count (parser couldn't classify the type → category='Review').
      // Fault-tolerant: a failure here must never block the rest of the dashboard.
      const reviewPromise = transactionService.getReviewTransactions().catch(() => ({ data: { total: 0, transactions: [] } }));
      // Cash rows waiting on the Portfolio tab (deposits above all) — they were
      // invisible to the counter, and a user whose deposits waited had a dead
      // curve with nothing on the dashboard saying so (3 Oct 2026).
      const portfolioQueuePromise = transactionService.getPortfolioQueueCount().catch(() => 0);

      const [navRes, tradesRes, openTradesRes, unreconciledClosesRes, tradeTransactionsRes, reviewRes, portfolioQueueCount] = await Promise.all([
        navPromise,
        tradesPromise,
        openTradesPromise,
        unreconciledClosesPromise,
        tradeTransactionsPromise,
        reviewPromise,
        portfolioQueuePromise
      ]);

      // Get all trades
      const allTrades = tradesRes.data?.trades || [];

      // Apply filters client-side. Shares held before the records, valued at
      // what they sold for (15 Oct 2026), carry no real result — they stay
      // out of win rate, R and trade counts.
      let filteredTrades = allTrades.filter(t => t.metadata?.opening_position?.cost_source !== 'sale');

      // Date filter — dashboard audit 2026-07-11 (JC decisions):
      //
      // BASIS: a trade belongs to the period its P&L was REALIZED in —
      // exit_date when the trade has one (Closed/Partial), entry_date for
      // Open trades. The old entry-date basis made the Net P/L card disagree
      // with the NAV table / heatmap / Performance Summary rows (all
      // exit-date) for any trade spanning a period boundary.
      //
      // BOUNDARIES: compared as LOCAL calendar-date strings ('YYYY-MM-DD'
      // compares lexically). The old code round-tripped local dates through
      // toISOString (UTC), which shifted Brisbane boundaries back a day —
      // YTD included Dec 31 of the prior year, custom ranges dropped trades
      // at both ends, and "Last 7 Days" spanned 8 days.
      if (dateParams.startDate || dateParams.endDate) {
        filteredTrades = filteredTrades.filter(t => {
          const raw = t.exit_date || t.entry_date || t.created_at;
          if (!raw) return false;
          const d = toLocalDateStr(new Date(raw));
          if (dateParams.startDate && d < dateParams.startDate) return false;
          if (dateParams.endDate && d > dateParams.endDate) return false;
          return true;
        });
      }

      // System filter
      if (filters.systems?.length > 0) {
        filteredTrades = filteredTrades.filter(t =>
          filters.systems.includes(t.trading_system) ||
          filters.systems.includes(t.system?.system_name)
        );
      }

      // Setup filter
      if (filters.setups?.length > 0) {
        filteredTrades = filteredTrades.filter(t =>
          filters.setups.includes(t.setup)
        );
      }

      // Item 88 — entry-type filter (pyramids are the same setup, different
      // entry class; 'all' keeps full setup economics)
      if (filters.entryType === 'initial') {
        filteredTrades = filteredTrades.filter(t => t.is_pyramid !== true);
      } else if (filters.entryType === 'pyramid') {
        filteredTrades = filteredTrades.filter(t => t.is_pyramid === true);
      }

      // Direction filter (Long/Short)
      if (filters.direction && filters.direction !== 'all') {
        filteredTrades = filteredTrades.filter(t => t.direction === filters.direction);
      }

      // Asset type filter (Stocks/CFD/ETF/…)
      if (filters.assetType && filters.assetType !== 'all') {
        filteredTrades = filteredTrades.filter(t => t.asset_type === filters.assetType);
      }

      // Trades with REALIZED P&L — dashboard audit 2026-07-11: ['Closed',
      // 'Partial'], matching the backend-wide basis (commit 7e27dc5 / F14).
      // A Partial trade's net_pl_account holds the realized portion only, so
      // it is safe to sum. The old Closed-only filter made the Trade P/L card
      // sit A$10k+ below FUM/NAV on the same screen for partial-exit users.
      const closedTrades = filteredTrades.filter(t => REALIZED_STATUSES.includes(t.status));

      // Trade P/L from realized trades. net_pl_reporting is the backend's
      // priority-based FX conversion; NULL means the conversion failed (F18
      // pattern) — EXCLUDE it rather than falling back to the unconverted
      // account amount (an AUD 5,000 must not be summed as "US$5,000").
      // undefined means the endpoint didn't convert at all (legacy shape) —
      // only then is net_pl_account the right value.
      // Item 87 (D-9, Option B): count the excluded trades so the card can say
      // "N trades excluded (no FX rate)" instead of showing a silently smaller sum.
      let fxExcludedTradeCount = 0;
      const netPnL = closedTrades.reduce((sum, t) => {
        if (t.net_pl_reporting === null) { fxExcludedTradeCount++; return sum; } // FX miss — excluded
        const pnl = t.net_pl_reporting !== undefined ? t.net_pl_reporting : t.net_pl_account;
        return sum + (parseFloat(pnl) || 0);
      }, 0);

      // Total R from realized trades. r_multiple is only stamped when a trade
      // fully closes, so for Partial trades derive realized R from the same
      // account-currency ratio (net_pl_account / risk_account) — otherwise the
      // R card lags the P/L card for every partial exit.
      const totalR = closedTrades.reduce((sum, t) => {
        const stamped = parseFloat(t.r_multiple);
        if (Number.isFinite(stamped)) return sum + stamped;
        const risk = parseFloat(t.risk_account);
        const pnl = parseFloat(t.net_pl_account);
        if (t.status === 'Partial' && Number.isFinite(risk) && risk > 0 && Number.isFinite(pnl)) {
          return sum + pnl / risk;
        }
        return sum;
      }, 0);

      // Active/open trades - ALWAYS unfiltered (current open positions)
      // This should not be affected by date range or other filters
      const openTrades = openTradesRes.data?.trades || [];
      const activeTradesCount = openTrades.length;

      // Unreconciled counts
      // Trades = unmatched close transactions (exits needing to be matched to entries)
      const unreconciledTradesCount = unreconciledClosesRes.data?.closes?.length || 0;
      // Transactions = unmatched open transactions (entries not yet created as
      // trades) PLUS the cash rows waiting on the Portfolio tab.
      const unreconciledTransactionsCount = (tradeTransactionsRes.data?.opens?.length || 0) + (Number(portfolioQueueCount) || 0);
      // Awaiting review = transactions the parser couldn't classify (category='Review')
      const reviewCount = reviewRes?.data?.total ?? reviewRes?.data?.transactions?.length ?? 0;

      // NAV data
      const navData = navRes.data || {};
      const fum = navData.currentNavReporting || navData.currentNav || 0;
      // Item 61: CAGR is the ANNUALIZED return (backend computes cagrPct from
      // cumulative return + track-record length). Was wrongly set to the raw
      // cumulativeReturnPct (un-annualized). Fall back to cumulative only if
      // the backend didn't supply cagrPct (older response shape).
      const cagr = (navData.cagrPct !== undefined && navData.cagrPct !== null)
        ? navData.cagrPct
        : (navData.cumulativeReturnPct || 0);

      // Item 62: Return on Capital (money-weighted) — the trader's actual %
      // gain/loss on deposited capital. Complements CAGR (time-weighted). Null
      // when the backend can't compute it (no deposits) → card shows "—".
      const returnOnCapital = (navData.returnOnCapitalPct !== undefined && navData.returnOnCapitalPct !== null)
        ? navData.returnOnCapitalPct
        : null;

      return {
        netPnL,
        fxExcludedTradeCount, // Item 87 D-9 — trades dropped from netPnL (no FX rate)
        totalR,
        fum,
        activeTrades: activeTradesCount, // Always unfiltered - current open positions
        cagr,
        // Item 87: full-history CAGR context (backend sends these only when
        // imported history exists) — drives the Self-reported badge + tooltip.
        cagrSelfReported: navData.cagrSelfReported || false,
        sinceJoiningCagr: (navData.sinceJoiningCagrPct !== undefined && navData.sinceJoiningCagrPct !== null)
          ? navData.sinceJoiningCagrPct : null,
        trackRecordStartDate: navData.trackRecordStartDate || null,
        // The date the CAGR counts from — the card says "since Mon YYYY" (2 Nov 2026)
        cagrSince: (navData.cagrSelfReported ? navData.trackRecordStartDate : navData.initializationDate) || null,
        returnOnCapital,
        unreconciledTradesCount,
        unreconciledTransactionsCount,
        reviewCount,
        totalTrades: allTrades.length,
        filteredTrades: filteredTrades.length,
        closedTradesCount: closedTrades.length,
        dateRange: filters.dateRange, // Pass through for label display
        // Include filtered trades for analysis charts
        filteredTradesData: filteredTrades,
        // Include all trades for Performance Summary Table (unfiltered)
        allTradesData: allTrades,
        // Constant filter options derived from THIS user's trades (so nobody sees a
        // type/direction they don't trade). Merged into filterOptions by the reducer.
        assetTypeOptions: [...new Set(allTrades.map(t => t.asset_type).filter(Boolean))].sort(),
        directionOptions: [...new Set(allTrades.map(t => t.direction).filter(Boolean))].sort(),
        // Dashboard audit 2026-07-11 (M1): systems/setups seen on ACTUAL trades.
        // The settings-sourced lists miss imported trades whose system/setup
        // was never registered — those trades were unfilterable. The reducer
        // unions these with the settings lists.
        systemOptions: [...new Set(allTrades.map(t => t.trading_system || t.system?.system_name).filter(Boolean))].sort(),
        setupOptions: [...new Set(allTrades.map(t => t.setup).filter(Boolean))].sort()
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch dashboard metrics');
    }
  }
);

// ── One stats engine (28 Oct 2026) ──────────────────────────────────────────
// When GET /nav/config says `stats_engine` is on for this person, the page
// takes this path instead of fetchDashboardMetrics: no 10,000-trade download,
// every trade statistic comes from /stats/* with the FilterBar's lens, and the
// components only display what the engine returns. The curve numbers (FUM,
// CAGR, return on capital) and the counters are read exactly as before.
export const fetchStatsMetrics = createAsyncThunk(
  'dashboard/fetchStatsMetrics',
  async ({ filters: filtersOverride, reportingCurrency = 'AUD' } = {}, { getState, rejectWithValue }) => {
    try {
      const filters = filtersOverride || getState().dashboard.filters || {};

      const navPromise = navService.getCurrentFromSnapshots({ reportingCurrency });
      const openTradesPromise = tradeService.getOpenTrades(null, { rollupStrategies: true });
      const unreconciledClosesPromise = tradeService.getAllUnmatchedCloses();
      const tradeTransactionsPromise = transactionService.getTradeTransactions();
      const reviewPromise = transactionService.getReviewTransactions().catch(() => ({ data: { total: 0, transactions: [] } }));
      const portfolioQueuePromise = transactionService.getPortfolioQueueCount().catch(() => 0);

      const [
        navRes, openTradesRes, unreconciledClosesRes, tradeTransactionsRes, reviewRes, portfolioQueueCount,
        options, summary, periods, groupsBySystem, groupsBySetup, monthly, distribution
      ] = await Promise.all([
        navPromise, openTradesPromise, unreconciledClosesPromise, tradeTransactionsPromise, reviewPromise, portfolioQueuePromise,
        statsService.getOptions(),
        statsService.getSummary(filters, reportingCurrency),
        statsService.getPeriods(filters, reportingCurrency),
        statsService.getGroups(filters, reportingCurrency, 'system'),
        statsService.getGroups(filters, reportingCurrency, 'setup'),
        statsService.getMonthly(filters, reportingCurrency),
        statsService.getDistribution(filters, reportingCurrency)
      ]);

      const openTrades = openTradesRes.data?.trades || [];
      const unreconciledTradesCount = unreconciledClosesRes.data?.closes?.length || 0;
      const unreconciledTransactionsCount = (tradeTransactionsRes.data?.opens?.length || 0) + (Number(portfolioQueueCount) || 0);
      const reviewCount = reviewRes?.data?.total ?? reviewRes?.data?.transactions?.length ?? 0;

      const navData = navRes.data || {};
      const fum = navData.currentNavReporting || navData.currentNav || 0;
      const cagr = (navData.cagrPct !== undefined && navData.cagrPct !== null) ? navData.cagrPct : (navData.cumulativeReturnPct || 0);
      const returnOnCapital = (navData.returnOnCapitalPct !== undefined && navData.returnOnCapitalPct !== null) ? navData.returnOnCapitalPct : null;

      const closed = summary?.closed_trades || {};
      const r = closed.r || {};
      return {
        metrics: {
          netPnL: summary?.realised?.amount ?? 0,
          fxExcludedTradeCount: summary?.realised?.fx_missing || 0,
          // null when no trade in the lens has an R — the card shows "-" (R7)
          totalR: r.total ?? null,
          rCoverage: { withR: r.with_r || 0, withoutR: r.without_r || 0, closed: closed.count || 0 },
          fum,
          activeTrades: openTrades.length,
          cagr,
          cagrSelfReported: navData.cagrSelfReported || false,
          sinceJoiningCagr: (navData.sinceJoiningCagrPct !== undefined && navData.sinceJoiningCagrPct !== null) ? navData.sinceJoiningCagrPct : null,
          trackRecordStartDate: navData.trackRecordStartDate || null,
          cagrSince: (navData.cagrSelfReported ? navData.trackRecordStartDate : navData.initializationDate) || null,
          returnOnCapital,
          unreconciledTradesCount,
          unreconciledTransactionsCount,
          reviewCount,
          totalTrades: options?.total_trades || 0,
          filteredTrades: summary?.coverage?.trades_considered ?? 0,
          closedTradesCount: closed.count || 0,
          dateRange: filters.dateRange,
          // The engine path downloads no trades — the components read `stats`.
          filteredTradesData: [],
          allTradesData: []
        },
        stats: {
          currency: summary?.currency || reportingCurrency,
          lens: summary?.lens || null,
          options, summary, periods, groupsBySystem, groupsBySetup, monthly, distribution
        },
        // Every FilterBar dropdown comes from /stats/options (all-time).
        filterOptions: {
          systems: options?.systems || [],
          setups: options?.setups || [],
          assetTypes: options?.asset_types || [],
          directions: options?.directions || []
        }
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch dashboard statistics');
    }
  }
);

// The one call the page makes to (re)load the trade statistics: picks the
// path from the flag in the store (fetchStatsMetrics when the engine is on
// for this person, the old fetchDashboardMetrics otherwise). Filters are read
// from the store by both thunks, so a reload after reconciliation keeps the
// current filters.
export const refreshMetrics = ({ reportingCurrency } = {}) => (dispatch, getState) => {
  const engineOn = getState().dashboard.statsEngine === true;
  return dispatch(engineOn ? fetchStatsMetrics({ reportingCurrency }) : fetchDashboardMetrics({ reportingCurrency }));
};

// Async thunk to fetch equity curve data
export const fetchEquityCurveData = createAsyncThunk(
  'dashboard/fetchEquityCurve',
  async ({ reportingCurrency = 'USD', systemNames = [] }, { rejectWithValue }) => {
    try {
      // Fetch portfolio NAV snapshots for equity curve.
      // Item 87: limit 0 = FULL chain. The old limit:120 silently truncated
      // anything past 10 years — a 15-year imported track record rendered
      // with its oldest 5 years missing from the equity curve, heatmap AND
      // drawdown (they all share this fetch). The walk is memoized
      // server-side; the payload is ~a few KB per year.
      const snapshotsRes = await navService.getSnapshotsWithReportingPnl({ limit: 0, reportingCurrency });

      // Fetch historical NAV data for each requested system (in reporting currency)
      const systemHistoryMap = {};
      if (systemNames.length > 0) {
        const systemHistoryPromises = systemNames.map(async (systemName) => {
          try {
            const historyRes = await navService.getSystemNavHistory(systemName, { reportingCurrency });
            return { systemName, history: historyRes.data || historyRes || [] };
          } catch (err) {
            console.warn(`Failed to fetch history for system ${systemName}:`, err);
            return { systemName, history: [] };
          }
        });
        const systemHistories = await Promise.all(systemHistoryPromises);
        systemHistories.forEach(({ systemName, history }) => {
          systemHistoryMap[systemName] = history;
        });
      }

      return {
        snapshots: snapshotsRes.data || [],
        systemHistory: systemHistoryMap
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch equity curve data');
    }
  }
);

// Helper: derive drawdown history from NAV snapshots (pure function of NAV/unit sequence).
// Used by the fetchEquityCurveData reducer so the drawdown chart reuses the snapshots
// that endpoint already fetched — no second call to /nav/snapshots-reporting.
function computeDrawdownHistory(snapshots) {
  const sorted = [...(snapshots || [])].sort((a, b) =>
    new Date(a.snapshot_date) - new Date(b.snapshot_date)
  );

  let peakNavPerUnit = 0;
  let startCounted = false;
  const out = [];
  for (const snapshot of sorted) {
    const date = new Date(snapshot.snapshot_date);
    // The fund starts at its starting price (1.00): an account that began with
    // its first deposit has no start row, so a fall in its first month was
    // never a drawdown (15 Oct 2026). Imported-history months come before it.
    if (!startCounted && snapshot.snapshot_type !== 'historical_import') {
      startCounted = true;
      const start = parseFloat(snapshot.starting_nav_per_unit_reporting);
      if (Number.isFinite(start) && start > peakNavPerUnit) peakNavPerUnit = start;
    }
    // Item 87: null nav/unit (an imported-history month whose FX rate is
    // missing) is a GAP, not a value — the old `|| 0` turned it into a
    // -100% drawdown spike. Skip the point; the peak carries across.
    const raw = parseFloat(snapshot.nav_per_unit_reporting);
    if (!Number.isFinite(raw) || raw <= 0) continue;
    const navPerUnit = raw;
    if (navPerUnit > peakNavPerUnit) peakNavPerUnit = navPerUnit;
    const drawdown = peakNavPerUnit > 0
      ? ((navPerUnit - peakNavPerUnit) / peakNavPerUnit) * 100
      : 0;
    out.push({
      date: date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
      fullDate: date,
      portfolioDrawdown: drawdown, // Negative or zero
      navPerUnit,
      selfReported: !!(snapshot.self_reported || snapshot.snapshot_type === 'historical_import')
    });
  }
  return out;
}

// The portfolio drawdown's seed — the starting price (1.00) of the first live
// snapshot — so a system's drawdown line can start from the same peak the
// portfolio does (28 Oct 2026, stats plan F6) instead of from 0.
function drawdownSeedOf(snapshots) {
  const sorted = [...(snapshots || [])].sort((a, b) => new Date(a.snapshot_date) - new Date(b.snapshot_date));
  const first = sorted.find(s => s.snapshot_type !== 'historical_import');
  const start = first ? parseFloat(first.starting_nav_per_unit_reporting) : NaN;
  return Number.isFinite(start) && start > 0 ? start : 0;
}

// Async thunk to fetch risk and drawdown data
// NOTE: snapshots are NOT fetched here — drawdown history is derived in the
// fetchEquityCurveData.fulfilled reducer from the snapshots that thunk already fetches.
// This eliminates a duplicate call to /nav/snapshots-reporting?limit=120 per Dashboard load.
export const fetchRiskDrawdownData = createAsyncThunk(
  'dashboard/fetchRiskDrawdown',
  async ({ reportingCurrency = 'USD' }, { rejectWithValue }) => {
    try {
      // Fetch open trades, NAV, and current FX rate for risk calculation.
      // (drawdown history comes from equity-curve snapshots via the reducer)
      //
      // 5d: switched from /nav/current (calculateCurrentNav — 100+ serial FX lookups,
      // no fxCache, ~3-5s) to /nav/current-snapshot (Phase 3 hot path — reads cached
      // line items, ~400ms). Same fum value, much faster.
      const [openTradesRes, navRes, fxRatesRes] = await Promise.all([
        // rollup=strategies: a defined-risk spread carries its risk on the
        // STRATEGY — its legs have live_risk NULL by design — so summing raw
        // leg risk reported 0% for a system holding a real $2K-risk spread
        // (and counted 2 positions instead of 1). The rolled row exposes the
        // strategy's defined risk as live_risk, in the legs' account currency.
        tradeService.getOpenTrades(null, { rollupStrategies: true }),
        navService.getCurrentFromSnapshots({ reportingCurrency }),
        fxService.getCurrentRates()
      ]);

      const openTrades = openTradesRes.data?.trades || [];
      const navData = navRes.data || {};
      const fum = navData.currentNavReporting || navData.currentNav || 0;

      // Item 60: live_risk is stored in the trade's ACCOUNT currency, not AUD.
      // The old code assumed AUD and multiplied every trade's risk by the
      // AUD→reporting rate, which deflated account-currency risk (e.g. a USD
      // trade's USD risk got shrunk by AUD→USD ≈ 0.72), understating the
      // risk-by-system percentages. Same bug class as Item 40 on the Trades
      // page. Fix: convert each trade from ITS account_currency to the
      // reporting currency. fxRatesRes.data.rates is [{fromCurrency,
      // toCurrency, rate}]. Build a per-currency lookup; same currency → 1;
      // unknown pair → 1 (don't silently deflate).
      const fxRatesArray = fxRatesRes.data?.rates || [];
      const rateFor = (fromCcy) => {
        if (!fromCcy || fromCcy === reportingCurrency) return 1;
        const hit = fxRatesArray.find(r => r.fromCurrency === fromCcy && r.toCurrency === reportingCurrency);
        if (hit?.rate) return hit.rate;
        // Dashboard audit 2026-07-11 (M6): try the INVERSE pair before
        // defaulting — the store may hold only one direction, and assuming
        // parity misclassifies risk (a £10k risk counted as $10k).
        const inverse = fxRatesArray.find(r => r.fromCurrency === reportingCurrency && r.toCurrency === fromCcy);
        if (inverse?.rate && parseFloat(inverse.rate) > 0) return 1 / parseFloat(inverse.rate);
        console.warn(`[Risk] no FX rate for ${fromCcy}→${reportingCurrency} — risk counted at parity`);
        return 1;
      };

      // Calculate risk by system from open trades using live_risk field
      const systemRiskMap = {};
      openTrades.forEach(trade => {
        const system = trade.trading_system || 'Unknown';
        if (!systemRiskMap[system]) {
          systemRiskMap[system] = {
            system,
            totalRisk: 0,
            openTrades: 0,
            unrealizedPnL: 0
          };
        }

        // Item 60: convert live_risk from the trade's account currency.
        const liveRiskNative = parseFloat(trade.live_risk) || 0;
        const liveRiskConverted = liveRiskNative * rateFor(trade.account_currency);
        const unrealizedPnL = parseFloat(trade.unrealized_pnl) || 0;

        systemRiskMap[system].totalRisk += liveRiskConverted;
        systemRiskMap[system].openTrades += 1;
        systemRiskMap[system].unrealizedPnL += unrealizedPnL;
      });

      // Convert to array with percentages
      const systemRiskData = Object.values(systemRiskMap).map(s => ({
        system: s.system,
        riskPercent: fum > 0 ? (s.totalRisk / fum) * 100 : 0,
        drawdownPercent: fum > 0 ? Math.abs(Math.min(0, s.unrealizedPnL)) / fum * 100 : 0,
        openTrades: s.openTrades,
        totalRisk: s.totalRisk,
        unrealizedPnL: s.unrealizedPnL
      }));

      return {
        systemRiskData,
        fum
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch risk/drawdown data');
    }
  }
);

// Async thunk to fetch benchmark monthly returns (S&P 500)
export const fetchBenchmarkData = createAsyncThunk(
  'dashboard/fetchBenchmark',
  async ({ symbol = 'SPY' } = {}, { rejectWithValue }) => {
    try {
      const result = await benchmarkService.getHeatmapData(symbol);
      return {
        symbol,
        data: result.data || []
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch benchmark data');
    }
  }
);

// Dashboard audit 2026-07-11 — local calendar-date string ('YYYY-MM-DD') from
// a Date's LOCAL components. Never round-trip through toISOString: for UTC+10
// users that renders local Jan-1 as Dec-31 and corrupts every period boundary.
// 'YYYY-MM-DD' strings compare correctly with < / > (lexicographic = chronological).
function toLocalDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Helper function to calculate date range. Returns LOCAL date strings; both
// ends inclusive (the filter compares whole calendar days, so a trade exited
// any time on the end date is included).
function getDateRangeParams(dateRange, customStart, customEnd) {
  const now = new Date();
  let startDate, endDate;

  switch (dateRange) {
    case 'Last7Days':
      // 7 calendar days INCLUDING today (was -7 → an 8-day window)
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 6);
      break;
    case 'Last30Days':
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 29);
      break;
    case 'QTD':
      startDate = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
      break;
    case 'YTD':
      startDate = new Date(now.getFullYear(), 0, 1);
      break;
    case 'AllTime':
      startDate = null;
      break;
    case 'Custom':
      // customStart/customEnd arrive as 'YYYY-MM-DD' from the date inputs —
      // pass through verbatim (already local calendar dates).
      return {
        startDate: customStart || undefined,
        endDate: customEnd || undefined
      };
    default:
      startDate = new Date(now.getFullYear(), 0, 1); // Default to YTD
  }

  return {
    startDate: startDate ? toLocalDateStr(startDate) : undefined,
    endDate: endDate ? toLocalDateStr(endDate) : undefined
  };
}

// Dashboard audit 2026-07-11 (C1): THE single source of truth for default
// filters. Previously three different "defaults" existed (initialState said
// AllTime, the Clear button fetched YTD, FilterBar treated YTD as inactive) —
// clicking Clear showed YTD data captioned "All Time (filtered)" and the
// Active chip could never turn off. Exported for FilterBar + DashboardPage.
export const DEFAULT_FILTERS = {
  dateRange: 'YTD',
  customStartDate: null,
  customEndDate: null,
  systems: [],
  setups: [],
  direction: 'all',
  assetType: 'all',
  entryType: 'all' // Item 88 — 'all' | 'initial' | 'pyramid'
};

// Realized-P&L trade statuses — matches the backend-wide basis (F14 / 7e27dc5).
const REALIZED_STATUSES = ['Closed', 'Partial'];

// JC dashboard catch 2026-07-18: the settings API returns OBJECTS ({id,
// value, ...}) while trade-derived options are STRINGS. `new Set` dedupes
// strings but never objects (fresh references every fetch), so each
// dashboard visit appended another copy of every settings system/setup —
// the chip lists grew forever. Normalize BOTH sides (and any polluted
// existing state) to trimmed value-strings before deduping.
const normalizeOptions = (...lists) => [...new Set(
  lists.flat().map(s => String((s && s.value) != null ? s.value : (s ?? '')).trim()).filter(Boolean)
)].sort();

const initialState = {
  // One stats engine (28 Oct 2026): null = not known yet (GET /nav/config
  // pending), true = this person's statistics come from /stats/*, false = the
  // old in-browser path. Set by the page from the config it already reads.
  statsEngine: null,
  // Engine responses, exactly as returned (the components display, no maths).
  stats: {
    currency: null,
    lens: null,
    options: null,
    summary: null,
    periods: null,
    groupsBySystem: null,
    groupsBySetup: null,
    monthly: null,
    distribution: null
  },
  filters: { ...DEFAULT_FILTERS },
  filterOptions: {
    systems: [],
    setups: [],
    // Populated from the user's own trades by the metrics reducer.
    assetTypes: [],
    directions: []
  },
  metrics: {
    netPnL: 0,
    totalR: 0,
    fum: 0,
    activeTrades: 0, // Always unfiltered - current open positions
    cagr: 0,
    returnOnCapital: null,
    unreconciledTradesCount: 0,
    unreconciledTransactionsCount: 0,
    reviewCount: 0,
    totalTrades: 0,
    filteredTrades: 0,
    closedTradesCount: 0,
    dateRange: 'YTD',
    filteredTradesData: [], // Actual trade objects for analysis charts
    allTradesData: [] // All trades for Performance Summary Table
  },
  // Sparkline data for metrics
  sparklines: {
    netPnL: [],
    totalR: [],
    fum: [],
    activeTrades: [],
    cagr: []
  },
  // Equity curve data
  equityCurve: {
    snapshots: [],
    systemHistory: {} // { systemName: [{ snapshotDate, navPerUnit, ... }] }
  },
  // Benchmark data (S&P 500)
  benchmark: {
    symbol: 'SPY',
    data: [] // [{ period_label, period_return_pct }]
  },
  // Risk and drawdown data
  riskDrawdown: {
    systemRiskData: [],
    drawdownHistory: [],
    drawdownSeed: 0, // the portfolio's starting price, the peak a drawdown line starts from
    fum: 0
  },
  // Capital deployment data
  capitalDeployment: {
    months: [],
    systems: [],
    deploymentData: [],
    summary: { avgDeployment: 0, maxDeployment: 0, currentDeployment: 0 }
  },
  loading: {
    filterOptions: false,
    metrics: false,
    equityCurve: false,
    riskDrawdown: false,
    capitalDeployment: false,
    monthlyReturns: false,
    benchmark: false
  },
  // Latest-wins guard: the in-flight request id per fetch. A `fulfilled` action
  // only applies its payload if its requestId still matches — so an out-of-order
  // OLDER response can't clobber newer data (the "wrong numbers until hard
  // refresh" bug). Pure string compare; no perf cost.
  requestIds: {
    filterOptions: null,
    metrics: null,
    riskDrawdown: null,
    capitalDeployment: null,
    benchmark: null
  },
  error: null
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        ...initialState.filters
      };
    },
    setDateRange: (state, action) => {
      state.filters.dateRange = action.payload;
    },
    setCustomDateRange: (state, action) => {
      state.filters.customStartDate = action.payload.startDate;
      state.filters.customEndDate = action.payload.endDate;
      state.filters.dateRange = 'Custom';
    },
    toggleSystem: (state, action) => {
      const system = action.payload;
      const index = state.filters.systems.indexOf(system);
      if (index === -1) {
        state.filters.systems.push(system);
      } else {
        state.filters.systems.splice(index, 1);
      }
    },
    toggleSetup: (state, action) => {
      const setup = action.payload;
      const index = state.filters.setups.indexOf(setup);
      if (index === -1) {
        state.filters.setups.push(setup);
      } else {
        state.filters.setups.splice(index, 1);
      }
    },
    setDirection: (state, action) => {
      state.filters.direction = action.payload;
    },
    setAssetType: (state, action) => {
      state.filters.assetType = action.payload;
    },
    setStatsEngine: (state, action) => {
      state.statsEngine = action.payload === null || action.payload === undefined ? null : !!action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch filter options
      .addCase(fetchFilterOptions.pending, (state, action) => {
        state.requestIds.filterOptions = action.meta.requestId;
        state.loading.filterOptions = true;
        state.error = null;
      })
      .addCase(fetchFilterOptions.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.requestIds.filterOptions) return; // stale
        state.loading.filterOptions = false;
        // Engine path: the dropdowns are /stats/options alone — a settings
        // list arriving late must not add names with no trades.
        if (state.statsEngine === true) return;
        // M1: UNION with whatever the metrics thunk derived from actual
        // trades — plain spread would clobber trade-derived options when
        // this (settings-sourced) response resolves second.
        const merged = { ...state.filterOptions, ...action.payload };
        if (Array.isArray(action.payload.systems)) {
          merged.systems = normalizeOptions(state.filterOptions.systems || [], action.payload.systems);
        }
        if (Array.isArray(action.payload.setups)) {
          merged.setups = normalizeOptions(state.filterOptions.setups || [], action.payload.setups);
        }
        state.filterOptions = merged;
      })
      .addCase(fetchFilterOptions.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestIds.filterOptions) return; // stale
        state.loading.filterOptions = false;
        state.error = action.payload;
      })
      // Fetch metrics
      .addCase(fetchDashboardMetrics.pending, (state, action) => {
        state.requestIds.metrics = action.meta.requestId;
        state.loading.metrics = true;
        state.error = null;
      })
      .addCase(fetchDashboardMetrics.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.requestIds.metrics) return; // stale
        state.loading.metrics = false;
        state.metrics = action.payload;
        // Populate the constant filter dropdowns from this user's own trades.
        if (Array.isArray(action.payload.assetTypeOptions)) {
          state.filterOptions.assetTypes = action.payload.assetTypeOptions;
        }
        if (Array.isArray(action.payload.directionOptions)) {
          state.filterOptions.directions = action.payload.directionOptions;
        }
        // M1: union trade-derived systems/setups with the settings-sourced
        // lists (whichever thunk resolves last, the union is stable).
        if (Array.isArray(action.payload.systemOptions)) {
          state.filterOptions.systems = normalizeOptions(state.filterOptions.systems || [], action.payload.systemOptions);
        }
        if (Array.isArray(action.payload.setupOptions)) {
          state.filterOptions.setups = normalizeOptions(state.filterOptions.setups || [], action.payload.setupOptions);
        }
      })
      .addCase(fetchDashboardMetrics.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestIds.metrics) return; // stale
        state.loading.metrics = false;
        state.error = action.payload;
      })
      // Fetch metrics from the stats engine — shares the metrics request id
      // (latest-wins) and loading flag with the old thunk; one path per page.
      .addCase(fetchStatsMetrics.pending, (state, action) => {
        state.requestIds.metrics = action.meta.requestId;
        state.loading.metrics = true;
        state.error = null;
      })
      .addCase(fetchStatsMetrics.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.requestIds.metrics) return; // stale
        state.loading.metrics = false;
        state.metrics = action.payload.metrics;
        state.stats = action.payload.stats;
        // The dropdowns are the engine's lists, nothing unioned in.
        state.filterOptions = {
          systems: normalizeOptions(action.payload.filterOptions.systems),
          setups: normalizeOptions(action.payload.filterOptions.setups),
          assetTypes: action.payload.filterOptions.assetTypes,
          directions: action.payload.filterOptions.directions
        };
      })
      .addCase(fetchStatsMetrics.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestIds.metrics) return; // stale
        state.loading.metrics = false;
        state.error = action.payload;
      })
      // Fetch equity curve — NOT latest-wins guarded: systemHistory accumulates
      // across calls (the equity-curve system toggle merges one system at a
      // time), so dropping an out-of-order response would lose merged systems.
      .addCase(fetchEquityCurveData.pending, (state) => {
        state.loading.equityCurve = true;
        state.error = null;
      })
      .addCase(fetchEquityCurveData.fulfilled, (state, action) => {
        state.loading.equityCurve = false;
        state.equityCurve.snapshots = action.payload.snapshots;
        // Merge new system history with existing (so we don't lose previously fetched systems)
        state.equityCurve.systemHistory = {
          ...state.equityCurve.systemHistory,
          ...action.payload.systemHistory
        };
        // Derive drawdown history from the snapshots we just fetched — avoids a second
        // /nav/snapshots-reporting round-trip that fetchRiskDrawdownData used to make.
        state.riskDrawdown.drawdownHistory = computeDrawdownHistory(action.payload.snapshots);
        state.riskDrawdown.drawdownSeed = drawdownSeedOf(action.payload.snapshots);
      })
      .addCase(fetchEquityCurveData.rejected, (state, action) => {
        state.loading.equityCurve = false;
        state.error = action.payload;
      })
      // Fetch risk/drawdown
      .addCase(fetchRiskDrawdownData.pending, (state, action) => {
        state.requestIds.riskDrawdown = action.meta.requestId;
        state.loading.riskDrawdown = true;
        state.error = null;
      })
      .addCase(fetchRiskDrawdownData.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.requestIds.riskDrawdown) return; // stale
        state.loading.riskDrawdown = false;
        state.riskDrawdown.systemRiskData = action.payload.systemRiskData;
        state.riskDrawdown.fum = action.payload.fum;
        // drawdownHistory is now derived in fetchEquityCurveData.fulfilled
      })
      .addCase(fetchRiskDrawdownData.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestIds.riskDrawdown) return; // stale
        state.loading.riskDrawdown = false;
        state.error = action.payload;
      })
      // Fetch capital deployment
      .addCase(fetchCapitalDeploymentData.pending, (state, action) => {
        state.requestIds.capitalDeployment = action.meta.requestId;
        state.loading.capitalDeployment = true;
        state.error = null;
      })
      .addCase(fetchCapitalDeploymentData.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.requestIds.capitalDeployment) return; // stale
        state.loading.capitalDeployment = false;
        state.capitalDeployment.months = action.payload.months;
        state.capitalDeployment.systems = action.payload.systems;
        state.capitalDeployment.deploymentData = action.payload.deploymentData;
        state.capitalDeployment.summary = action.payload.summary;
      })
      .addCase(fetchCapitalDeploymentData.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestIds.capitalDeployment) return; // stale
        state.loading.capitalDeployment = false;
        state.error = action.payload;
      })
      // Fetch benchmark data
      .addCase(fetchBenchmarkData.pending, (state, action) => {
        state.requestIds.benchmark = action.meta.requestId;
        state.loading.benchmark = true;
        state.error = null;
      })
      .addCase(fetchBenchmarkData.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.requestIds.benchmark) return; // stale
        state.loading.benchmark = false;
        state.benchmark.symbol = action.payload.symbol;
        state.benchmark.data = action.payload.data;
      })
      .addCase(fetchBenchmarkData.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestIds.benchmark) return; // stale
        state.loading.benchmark = false;
        state.error = action.payload;
      });
  }
});

export const {
  setFilters,
  clearFilters,
  setDateRange,
  setCustomDateRange,
  toggleSystem,
  toggleSetup,
  setDirection,
  setAssetType,
  setStatsEngine
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
